import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaRupeeSign, FaShieldAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const FREE_TRIAL_PLAN = {
  key: "freeTrial",
  firebaseKey: "freeTrial",
  nameEn: "Free Trial",
  nameMr: "फ्री ट्रायल",
  price: 0,
  mrp: 0,
  studentLimit: 20,
  durationDays: 7,
  badgeEn: "7 Days Free",
  badgeMr: "७ दिवस मोफत",
  badgeColor: "#6f42c1",
  isTrial: true,
  featuresEn: [
    "7 Days Full Access",
    "Up to 20 Students Limit",
    "Student Record & Registration",
    "Bonafide Certificate Generation",
    "Leaving Certificate (LC / TC)",
    "No Payment / Card Required"
  ],
  featuresMr: [
    "७ दिवस मोफत पूर्ण वापर",
    "कमाल २० विद्यार्थी मर्यादा",
    "विद्यार्थी नोंदणी व रेकॉर्ड",
    "बोनाफाइड प्रमाणपत्र जनरेशन",
    "शाळा सोडल्याचा दाखला (LC)",
    "कोणतेही शुल्क आवश्यक नाही"
  ],
  lockedModules: {
    admission: false,
    attandance: false,
    fee: false,
    exam: false,
    poshanahar: false,
    lc: false,
    idcard: false,
    bonafide: false
  }
};

const DEFAULT_PAID_PLANS = [
  {
    key: "plan100",
    firebaseKey: "100 Students",
    nameEn: "100 Students Plan",
    nameMr: "१०० विद्यार्थी प्लॅन",
    price: 5000,
    mrp: 5000,
    studentLimit: 100,
    durationDays: 365,
    badgeEn: "Starter",
    badgeMr: "स्टार्टर",
    badgeColor: "#6c757d",
    isTrial: false,
    featuresEn: [
      "Up to 100 Students Limit",
      "Student Record & Registration",
      "Bonafide Certificate Generation",
      "Leaving Certificate (LC / TC)",
      "Student Identity Cards",
      "Standard Reports & Export"
    ],
    featuresMr: [
      "कमाल १०० विद्यार्थी मर्यादा",
      "विद्यार्थी नोंदणी व रेकॉर्ड",
      "बोनाफाइड प्रमाणपत्र",
      "शाळा सोडल्याचा दाखला (LC)",
      "विद्यार्थी ओळखपत्र",
      "मानक अहवाल व निर्यात"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  },
  {
    key: "plan250",
    firebaseKey: "250 Students",
    nameEn: "250 Students Plan",
    nameMr: "२५० विद्यार्थी प्लॅन",
    price: 10000,
    mrp: 10000,
    studentLimit: 250,
    durationDays: 365,
    badgeEn: "Basic",
    badgeMr: "बेसिक",
    badgeColor: "#0dcaf0",
    isTrial: false,
    featuresEn: [
      "Up to 250 Students Limit",
      "Student Record & Registration",
      "Bonafide Certificate Generation",
      "Leaving Certificate (LC / TC)",
      "Student Identity Cards",
      "Standard Reports & Export"
    ],
    featuresMr: [
      "कमाल २५० विद्यार्थी मर्यादा",
      "विद्यार्थी नोंदणी व रेकॉर्ड",
      "बोनाफाइड प्रमाणपत्र",
      "शाळा सोडल्याचा दाखला (LC)",
      "विद्यार्थी ओळखपत्र",
      "मानक अहवाल व निर्यात"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  },
  {
    key: "plan500",
    firebaseKey: "500 Students",
    nameEn: "500 Students Plan",
    nameMr: "५०० विद्यार्थी प्लॅन",
    price: 15000,
    mrp: 15000,
    studentLimit: 500,
    durationDays: 365,
    badgeEn: "Popular",
    badgeMr: "लोकप्रिय",
    badgeColor: "#ffc107",
    isTrial: false,
    featuresEn: [
      "Up to 500 Students Limit",
      "All Basic Features",
      "Fee Management & Receipts",
      "Daily Student Attendance",
      "Staff & Teacher Attendance",
      "Fee Due SMS & Notifications"
    ],
    featuresMr: [
      "कमाल ५०० विद्यार्थी मर्यादा",
      "बेसिक प्लॅनच्या सर्व सुविधा",
      "फी व्यवस्थापन व पावती",
      "दैनिक विद्यार्थी उपस्थिती",
      "शिक्षक व कर्मचारी उपस्थिती",
      "फी थकबाकी माहिती व सूचना"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  },
  {
    key: "plan750",
    firebaseKey: "750 Students",
    nameEn: "750 Students Plan",
    nameMr: "७५० विद्यार्थी प्लॅन",
    price: 20000,
    mrp: 20000,
    studentLimit: 750,
    durationDays: 365,
    badgeEn: "Growth",
    badgeMr: "ग्रोथ",
    badgeColor: "#fd7e14",
    isTrial: false,
    featuresEn: [
      "Up to 750 Students Limit",
      "All 500 Plan Features",
      "Exam & Marksheet System",
      "Progress Report Card Generator",
      "Daily Attendance & SMS",
      "Standard & Custom Reports"
    ],
    featuresMr: [
      "कमाल ७५० विद्यार्थी मर्यादा",
      "५०० प्लॅनच्या सर्व सुविधा",
      "परीक्षा आणि निकाल प्रणाली",
      "प्रगती पुस्तक जनरेटर",
      "दैनिक उपस्थिती व SMS",
      "मानक व कस्टम अहवाल"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  },
  {
    key: "plan1000",
    firebaseKey: "1000 Students",
    nameEn: "1000 Students Plan",
    nameMr: "१००० विद्यार्थी प्लॅन",
    price: 25000,
    mrp: 25000,
    studentLimit: 1000,
    durationDays: 365,
    badgeEn: "Advanced",
    badgeMr: "प्रगत",
    badgeColor: "#0d6efd",
    isTrial: false,
    featuresEn: [
      "Up to 1000 Students Limit",
      "All 750 Plan Features",
      "Exam & Marksheet System",
      "Class Rank & Analytics",
      "Custom Report Templates",
      "Priority Technical Support"
    ],
    featuresMr: [
      "कमाल १००० विद्यार्थी मर्यादा",
      "७५० प्लॅनच्या सर्व सुविधा",
      "परीक्षा आणि निकाल प्रणाली",
      "गुणवत्ता व कामगिरी विश्लेषण",
      "कस्टम रिपोर्ट टेम्पलेट्स",
      "प्राधान्य तांत्रिक सपोर्ट"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  },
  {
    key: "plan1500",
    firebaseKey: "1500 Students",
    nameEn: "1500 Students Plan",
    nameMr: "१५०० विद्यार्थी प्लॅन",
    price: 35000,
    mrp: 35000,
    studentLimit: 1500,
    durationDays: 365,
    badgeEn: "Premium",
    badgeMr: "प्रीमियम",
    badgeColor: "#6f42c1",
    isTrial: false,
    featuresEn: [
      "Up to 1500 Students Limit",
      "All Advanced Features",
      "Poshanahar (Midday Meal) Reports",
      "Daily Meal Inventory & Stock",
      "Government Compliance Export",
      "Priority Dedicated Support"
    ],
    featuresMr: [
      "कमाल १५०० विद्यार्थी मर्यादा",
      "सर्व प्रगत सुविधा",
      "पोषण आहार दैनिक नोंद व अहवाल",
      "धान्य व आहार साठा ट्रॅकिंग",
      "शासकीय नियम पूर्ण करणारे रीपोर्ट",
      "प्राधान्य ग्राहक सेवा"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  },
  {
    key: "plan2000",
    firebaseKey: "2000 Students",
    nameEn: "2000 Students Plan",
    nameMr: "२००० विद्यार्थी प्लॅन",
    price: 45000,
    mrp: 45000,
    studentLimit: 2000,
    durationDays: 365,
    badgeEn: "Ultimate",
    badgeMr: "अल्टिमेट",
    badgeColor: "#198754",
    isTrial: false,
    featuresEn: [
      "Up to 2000 Students Limit",
      "Complete All-in-One School Suite",
      "Full Poshanahar (Midday Meal) System",
      "Custom Report Templates",
      "Government Compliance Export",
      "24/7 Dedicated Priority Support"
    ],
    featuresMr: [
      "कमाल २००० विद्यार्थी मर्यादा",
      "सर्वसमावेशक शाळा व्यवस्थापन",
      "संपूर्ण पोषण आहार प्रणाली",
      "कस्टम रिपोर्ट टेम्पलेट्स",
      "शासकीय नियम पूर्ण करणारे रीपोर्ट",
      "24/7 प्राधान्य ग्राहक सेवा"
    ],
    lockedModules: {
      admission: false,
      attandance: false,
      fee: false,
      exam: false,
      poshanahar: false,
      lc: false,
      idcard: false,
      bonafide: false
    }
  }
];

const INITIAL_PLANS = [FREE_TRIAL_PLAN, ...DEFAULT_PAID_PLANS];

function Plansforuser() {
  const [plans, setPlans] = useState(INITIAL_PLANS);
  const [selectedPlanKey, setSelectedPlanKey] = useState("plan100");
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeactivationMessage, setShowDeactivationMessage] = useState(true);
  const [activeOffer, setActiveOffer] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const projectId = process.env.REACT_APP_FS_PROJECT_ID;
  const language = localStorage.getItem("language") || "English";
  const udise = location.state?.udiseNumber || localStorage.getItem('udiseNumber');

  // Fetch active offers
  const fetchActiveOffers = async () => {
    try {
      if (!projectId) return;
      const response = await fetch(
        `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/offersForAll`
      );
      if (!response.ok) return;

      const offersData = await response.json();
      const currentDate = new Date();

      if (offersData.documents) {
        const activeOffers = Object.values(offersData.documents)
          .map((doc) => {
            const fields = doc.fields;
            return {
              id: doc.name.split('/').pop(),
              title: fields.title?.stringValue || '',
              description: fields.description?.stringValue || '',
              startDate: fields.startDate?.stringValue || '',
              endDate: fields.endDate?.stringValue || '',
              minAmount: parseInt(fields.minAmount?.integerValue || '0', 10),
              percentage: parseFloat(fields.percentage?.doubleValue || fields.percentage?.integerValue || '0'),
              status: fields.status?.stringValue || ''
            };
          })
          .filter((offer) => {
            const startDate = new Date(offer.startDate);
            const endDate = new Date(offer.endDate);
            return offer.status === 'active' && currentDate >= startDate && currentDate <= endDate;
          });

        if (activeOffers.length > 0) {
          setActiveOffer(activeOffers[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching active offers:', error);
    }
  };

  useEffect(() => {
    fetchActiveOffers();
  }, []);

  // Fetch plans dynamically from Admin/purchesPlan
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        let adminData = null;

        // Try to fetch user-specific plans or general admin plans
        if (udise) {
          try {
            const userRes = await fetch(
              `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/Admin/${udise}/purchesPlan.json`
            );
            adminData = await userRes.json();
          } catch (e) {}
        }

        if (!adminData) {
          const generalRes = await fetch(
            `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/Admin/purchesPlan.json`
          );
          adminData = await generalRes.json();
        }

        if (adminData) {
          const mergedPaid = DEFAULT_PAID_PLANS.map((dp) => {
            const remote = adminData[dp.firebaseKey] || adminData[dp.key];
            if (remote) {
              const mrpVal = remote.mrp !== undefined ? parseInt(remote.mrp, 10) : dp.mrp;
              const fixRateVal =
                remote.fixRate !== undefined
                  ? parseInt(remote.fixRate, 10)
                  : remote.earlyRenewal !== undefined
                  ? parseInt(remote.earlyRenewal, 10)
                  : dp.price;
              const priceVal = isNaN(fixRateVal) ? (isNaN(mrpVal) ? dp.price : mrpVal) : fixRateVal;
              return {
                ...dp,
                price: priceVal,
                mrp: isNaN(mrpVal) ? dp.mrp : mrpVal,
                studentLimit: remote.studentLimit !== undefined ? remote.studentLimit : dp.studentLimit
              };
            }
            return dp;
          });
          setPlans([FREE_TRIAL_PLAN, ...mergedPaid]);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [udise]);

  const getAgentAndUdise = async (udiseNum) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/Admin/${udiseNum}.json`);
      if (!res.ok) return { agentId: null };
      const data = await res.json();
      return { agentId: data?.agentId || null };
    } catch (err) {
      return { agentId: null };
    }
  };

  const saveToFirestore = async (agentId, udiseNum, newData) => {
    if (!agentId || !projectId) return;
    const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/sells/${agentId}/schools/${udiseNum}`;

    try {
      const existingResponse = await fetch(url);
      let existingFields = {};
      if (existingResponse.ok) {
        const existingDoc = await existingResponse.json();
        existingFields = existingDoc.fields || {};
      }

      const newFields = {};
      Object.entries(newData).forEach(([key, value]) => {
        if (typeof value === 'string') {
          newFields[key] = { stringValue: value };
        } else if (typeof value === 'number') {
          newFields[key] = { integerValue: value.toString() };
        } else if (typeof value === 'boolean') {
          newFields[key] = { booleanValue: value };
        } else if (value && typeof value === 'object') {
          const mapFields = {};
          Object.entries(value).forEach(([mapKey, mapValue]) => {
            mapFields[mapKey] = { stringValue: String(mapValue) };
          });
          newFields[key] = { mapValue: { fields: mapFields } };
        }
      });

      const mergedFields = { ...existingFields, ...newFields };
      await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields: mergedFields })
      });
    } catch (error) {
      console.error('Error saving to Firestore:', error);
    }
  };

  const saveLockedModulesToRealtimeDB = async (udiseNum, locked) => {
    try {
      await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/Admin/${udiseNum}/lockedModules.json`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(locked || {})
      });
    } catch (e) {
      console.error(e);
    }
  };

  const updateAgentWalletPoints = async (agentId, commissionAmount) => {
    if (!agentId || !projectId || !commissionAmount) return;
    try {
      const walletPointsUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/agents/${agentId}/wallet/points`;
      const res = await fetch(walletPointsUrl);
      let total = 0;
      let currentMonth = 0;
      const monthYearKey = `${new Date().getMonth() + 1}_${new Date().getFullYear()}`;

      if (res.ok) {
        const data = await res.json();
        total = parseInt(data.fields?.totalWalletPoints?.integerValue || "0", 10);
        currentMonth = parseInt(data.fields?.[monthYearKey]?.integerValue || "0", 10);
      }

      await fetch(walletPointsUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: {
            totalWalletPoints: { integerValue: total + commissionAmount },
            [monthYearKey]: { integerValue: currentMonth + commissionAmount }
          }
        })
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handlePlanActivation = async (planObj) => {
    const selectedPlan = planObj || plans.find((p) => p.key === selectedPlanKey) || plans[0];
    const isTrial = selectedPlan.isTrial === true || selectedPlan.key === "freeTrial";

    if (!udise) {
      alert("UDISE Number is missing. Please log in again.");
      navigate("/");
      return;
    }

    try {
      setIsProcessing(true);

      // Handle Free Trial Activation
      if (isTrial || selectedPlan.price === 0) {
        const deactivationDate = new Date();
        deactivationDate.setDate(deactivationDate.getDate() + 7);

        const updateData = {
          packActivated: "freeTrial",
          finalPrize: 0,
          paidAmount: 0,
          studentLimit: selectedPlan.studentLimit || 20,
          userStatus: "Active",
          status: "Completed",
          activationDate: new Date().toISOString().split("T")[0],
          deactivationDate: deactivationDate.toISOString().split("T")[0]
        };

        await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/Admin/${udise}.json`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData)
        });

        await saveLockedModulesToRealtimeDB(udise, selectedPlan.lockedModules);

        alert(
          language === "English"
            ? "Free Trial activated successfully for 7 days!"
            : "७ दिवसांची फ्री ट्रायल यशस्वीरित्या सक्रिय झाली आहे!"
        );
        navigate("/home");
        return;
      }

      // Handle Paid Plan via Razorpay
      let amount = selectedPlan.price;
      if (activeOffer && amount >= (activeOffer.minAmount || 0)) {
        const discount = Math.round((amount * activeOffer.percentage) / 100);
        amount = Math.max(0, amount - discount);
      }

      const orderEndpoints = [
        "/backend/create_order.php",
        `${window.location.origin}/backend/create_order.php`,
        "http://localhost:8000/backend/create_order.php",
        "http://localhost/digitalshare/create_order.php"
      ];

      let response = null;
      for (const endpoint of orderEndpoints) {
        try {
          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              amount,
              plan: selectedPlan.nameEn,
              udise,
              offerId: activeOffer ? activeOffer.id : null
            })
          });
          if (res.ok) {
            response = res;
            break;
          }
        } catch (e) {}
      }

      if (!response || !response.ok) {
        throw new Error("Failed to create Razorpay payment order.");
      }

      const orderData = await response.json();

      const options = {
        key: "rzp_test_BSj1LLEYKca6Cb",
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.id,
        name: "SGK Brainova Software",
        description: `${selectedPlan.nameEn} Subscription`,
        handler: async function (payResponse) {
          const deactivationDate = new Date();
          deactivationDate.setDate(deactivationDate.getDate() + (selectedPlan.durationDays || 365));

          const packKey = selectedPlan.firebaseKey || selectedPlan.nameEn;
          const dataToSave = {
            packActivated: packKey,
            finalPrize: amount,
            paidAmount: amount,
            studentLimit: selectedPlan.studentLimit,
            lockedModules: selectedPlan.lockedModules,
            userStatus: "Active",
            status: "Completed",
            activationDate: new Date().toISOString().split("T")[0],
            deactivationDate: deactivationDate.toISOString().split("T")[0],
            razorpayOrderId: orderData.id,
            razorpayPaymentId: payResponse.razorpay_payment_id
          };

          const { agentId } = await getAgentAndUdise(udise);

          await Promise.all([
            fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/Admin/${udise}.json`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(dataToSave)
            }),
            saveLockedModulesToRealtimeDB(udise, selectedPlan.lockedModules),
            saveToFirestore(agentId, udise, dataToSave)
          ]);

          if (agentId) {
            try {
              const percentRes = await fetch(
                `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/agents/${agentId}`
              );
              if (percentRes.ok) {
                const percentData = await percentRes.json();
                const pVal = parseInt(percentData.fields?.dealershipPercentage?.stringValue || "0", 10);
                const comm = Math.floor(amount * (pVal / 100));
                await updateAgentWalletPoints(agentId, comm);
              }
            } catch (err) {
              console.error(err);
            }
          }

          alert(
            language === "English"
              ? "Payment successful! Your subscription is now active."
              : "पेमेंट यशस्वी झाले! आपले सबस्क्रिप्शन सक्रिय झाले आहे."
          );
          navigate("/home");
        },
        prefill: {
          name: localStorage.getItem("schoolName") || "School Admin",
          email: `${udise}@school.com`,
          contact: localStorage.getItem("mobile") || "9999999999"
        },
        theme: {
          color: selectedPlan.badgeColor || "#0d6efd"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment initiation error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedPlan = plans.find((p) => p.key === selectedPlanKey) || plans[0];
  const isCurrentTrial = selectedPlan.isTrial === true || selectedPlan.key === "freeTrial";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="main-content-of-page"
      style={{
        paddingTop: "90px",
        paddingBottom: "60px",
        paddingLeft: "15px",
        paddingRight: "15px",
        maxWidth: "1600px",
        margin: "0 auto",
        minHeight: "100vh",
        boxSizing: "border-box"
      }}
    >
      {/* Deactivation Banner */}
      {showDeactivationMessage && (
        <div
          className="alert alert-warning alert-dismissible fade show d-flex justify-content-between align-items-center shadow-sm rounded-4 mb-4 flex-wrap gap-2"
          role="alert"
          style={{ marginTop: "10px" }}
        >
          <div className="d-flex align-items-center gap-3">
            <FaShieldAlt className="fs-3 text-warning flex-shrink-0" />
            <div style={{ fontSize: "0.95rem" }}>
              <strong>
                {language === "English" ? "Subscription Notice: " : "सबस्क्रिप्शन सूचना: "}
              </strong>
              {language === "English"
                ? "Your current plan is expiring or deactivated. Please select a plan below to continue uninterrupted service."
                : "आपला चालू प्लॅन संपत आला आहे किंवा बंद झाला आहे. सेवा अखंड चालू ठेवण्यासाठी खालीलपैकी प्लॅन निवडा."}
            </div>
          </div>
          <button
            type="button"
            className="btn-close position-relative p-2"
            onClick={() => setShowDeactivationMessage(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Active Offer Banner */}
      {activeOffer && (
        <div className="alert alert-info shadow-sm rounded-4 mb-4 text-center">
          🎉 <strong>{activeOffer.title}</strong>: {activeOffer.description} (
          {activeOffer.percentage}% Discount on orders above ₹{activeOffer.minAmount})
        </div>
      )}

      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2"
      >
        <div>
          <h2 className="fw-bold text-dark m-0" style={{ fontSize: "1.75rem", lineHeight: "1.3" }}>
            {language === "English" ? "Select Subscription Plan" : "सबस्क्रिप्शन प्लॅन निवडा"}
          </h2>
          <p className="text-muted m-0 small mt-1">
            {language === "English"
              ? `Choose the best suited plan for your school (UDISE: ${udise || "N/A"})`
              : `आपल्या शाळेसाठी योग्य प्लॅन निवडा (UDISE: ${udise || "N/A"})`}
          </p>
        </div>
        <button
          className="btn btn-secondary px-4 py-2 fw-semibold rounded-3 shadow-sm"
          onClick={() => navigate(-1)}
        >
          {language === "English" ? "Back" : "मागे जा"}
        </button>
      </motion.div>

      {/* Plan Cards Grid (8 Cards as in Image 2) */}
      <div className="row g-3 justify-content-center mb-4">
        {plans.map((plan) => {
          const isSelected = selectedPlanKey === plan.key;
          const isTrial = plan.isTrial === true || plan.key === "freeTrial";

          return (
            <div
              className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3 d-flex align-items-stretch"
              key={plan.key}
              style={{ minWidth: "240px", maxWidth: "340px" }}
            >
              <div
                className={`card h-100 w-100 shadow-sm border-2 rounded-4 ${
                  isSelected ? "border-primary bg-white shadow-lg" : "border-light bg-light"
                }`}
                style={{
                  cursor: "pointer",
                  transform: isSelected ? "scale(1.02)" : "scale(1)",
                  transition: "all 0.25s ease-in-out",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden"
                }}
                onClick={() => setSelectedPlanKey(plan.key)}
              >
                {/* Header */}
                <div className="card-header bg-transparent border-0 text-center pt-3 pb-1 px-3">
                  <span
                    className="badge rounded-pill text-uppercase px-3 py-1 mb-2 text-white fw-bold"
                    style={{ backgroundColor: plan.badgeColor || "#007bff", fontSize: "0.75rem", letterSpacing: "0.5px" }}
                  >
                    {language === "English" ? plan.badgeEn : plan.badgeMr}
                  </span>
                  <h5 className="fw-bold my-1 text-dark text-truncate" title={language === "English" ? plan.nameEn : plan.nameMr} style={{ fontSize: "1.1rem" }}>
                    {language === "English" ? plan.nameEn : plan.nameMr}
                  </h5>

                  {/* Student Limit Badge */}
                  <div className="my-2">
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1 fw-bold fs-7">
                      👥 {plan.studentLimit} {language === "English" ? "Students" : "विद्यार्थी"}
                    </span>
                  </div>

                  {/* Price Display */}
                  <div className="my-1">
                    <span className="fs-3 fw-bold text-primary">
                      ₹{plan.price.toLocaleString("en-IN")}
                    </span>
                    <span className="text-muted small">
                      {" "}
                      / {isTrial
                        ? (language === "English" ? "7 days" : "७ दिवस")
                        : (language === "English" ? "year" : "वर्ष")}
                    </span>
                  </div>
                </div>

                {/* Body Features */}
                <div className="card-body d-flex flex-column px-3 pt-2 pb-3">
                  <ul className="list-unstyled mb-3 flex-grow-1">
                    {(language === "English" ? plan.featuresEn : plan.featuresMr).map((feat, idx) => (
                      <li
                        key={idx}
                        className="py-1 border-bottom border-light text-secondary small d-flex align-items-start"
                        style={{ lineHeight: "1.4" }}
                      >
                        <span className="text-success fw-bold me-2 flex-shrink-0">✓</span>
                        <span style={{ wordBreak: "break-word" }}>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Radio / Selection Indicator */}
                  <div className="form-check text-center mt-auto pt-2">
                    <input
                      className="form-check-input float-none me-2"
                      type="radio"
                      name="userPlanRadio"
                      id={`plan_user_${plan.key}`}
                      checked={isSelected}
                      onChange={() => setSelectedPlanKey(plan.key)}
                    />
                    <label
                      className="form-check-label fw-bold text-dark small"
                      htmlFor={`plan_user_${plan.key}`}
                    >
                      {isSelected
                        ? (language === "English" ? "Selected Plan" : "निवडलेला प्लॅन")
                        : (language === "English" ? "Select Plan" : "प्लॅन निवडा")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Footer CTA */}
      <div className="card shadow border-0 rounded-4 bg-white p-4 col-12 col-md-10 col-lg-8 mx-auto text-center mt-4">
        <div className="row align-items-center g-3">
          <div className="col-12 col-md-7 text-md-start">
            <h5 className="fw-bold text-dark mb-1">
              {language === "English" ? selectedPlan.nameEn : selectedPlan.nameMr}
            </h5>
            <p className="text-muted small mb-0">
              👥 {selectedPlan.studentLimit} {language === "English" ? "Students Limit" : "विद्यार्थी मर्यादा"} •{" "}
              {isCurrentTrial
                ? (language === "English" ? "7 Days Full Access" : "७ दिवस पूर्ण वापर")
                : (language === "English" ? "1 Year Full Access" : "१ वर्ष पूर्ण वापर")}
            </p>
          </div>
          <div className="col-12 col-md-5 text-md-end">
            <button
              className={`btn px-4 py-3 fw-bold rounded-3 shadow-sm w-100 ${
                isCurrentTrial ? "btn-success" : "btn-primary"
              }`}
              onClick={() => handlePlanActivation(selectedPlan)}
              disabled={isProcessing}
            >
              {isProcessing
                ? (language === "English" ? "Processing..." : "प्रक्रिया सुरू आहे...")
                : isCurrentTrial
                ? (language === "English" ? "Activate Free Trial" : "फ्री ट्रायल सक्रिय करा")
                : (language === "English"
                  ? `Pay ₹${selectedPlan.price.toLocaleString("en-IN")} to Activate`
                  : `सक्रिय करण्यासाठी ₹${selectedPlan.price.toLocaleString("en-IN")} भरा`)}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Plansforuser;
